import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
} from '@angular/core';
import { OrderformFascade } from '../../fascades/orderform.fascade';
import { filter, map, share, shareReplay, switchMap } from 'rxjs/operators';
import {
  BehaviorSubject,
  combineLatest,
  firstValueFrom,
  Observable,
  ReplaySubject,
  Subject,
} from 'rxjs';
import { OrderformState } from '../../../index';
import { ANIMATION_slideInOut } from '../../animations';
import { Store } from '@ngrx/store';
import { OrderformIframeService } from '../../services/orderform-iframe.service';
import {
  isElementInViewport,
  viewportVisibilityChange,
} from '@digistore/ds24-ui/rxjs';
import * as OrderformActions from '../../ngrx/orderform.actions';
import { IframeModalService } from '../iframe-modal/iframe-modal.service';
import { MonitoringService } from '../../services/monitoring.service';

export type TypeSummarySetting =
  | 'popup_if_needed'
  | 'above_if_needed'
  | 'above_always';

@Component({
  selector: 'orderform-buy-button',
  templateUrl: './buy-button.component.html',
  styleUrls: ['./buy-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [ANIMATION_slideInOut],
})
export class BuyButtonComponent implements OnDestroy, OnChanges, AfterViewInit {
  _blockButton$ = new BehaviorSubject(false);

  @Input() buyBtnTheme = null;
  @Input() buyBtnText = null;
  @Input() sealType: 'gold' | 'blue' | null = null;

  @Input() iconLeft = null;
  @Input() iconRight = null;

  /**
   * Summary setting
   */
  modalTitle = $localize`:@@buy_button.buy-verification.title:Sie bestellen`;
  modalVisible = false;
  @Input() summarySetting: TypeSummarySetting;
  @Input() summaryLayout: 'horizontal' | 'vertical' = 'horizontal';
  @Input() summaryOuterContainerClasses?: string;
  @Input() summaryInnerContainerClasses?: string;
  private _ngAfterViewInit$ = new Subject<void>();
  /**
   * Attention - dies attribute will be set via javascript. Not via hbs template!
   */
  @Input() set forceShowModal(status: 'true' | 'false') {
    if (status) {
      this.modalVisible = status === 'true';
    }
  }
  @Input() forceShowSummary: 'false' | 'true' | null = null; // null = not in editor

  @Input() set editorMode(value: string) {
    this._isTranslationMode = value === 'translation';
    this._isEditorMode = value === 'translation' || value === 'design';
  }

  get isTranslationMode(): boolean {
    return this._isTranslationMode;
  }

  private _isTranslationMode: boolean;
  private _isEditorMode = false;
  private _destroy$ = new Subject<void>();

  selectedPaymentMethod$ = this._orderformFascade.order$.pipe(
    map((e) => {
      return e.order.payment_provider;
    }),
    shareReplay(1)
  );

  buttonDisabled$ = combineLatest([
    this._blockButton$,
    this.selectedPaymentMethod$,
  ]).pipe(
    map(([blockButton, selectedMethod]) => {
      return blockButton || (this._isEditorMode && selectedMethod.disabled);
    })
  );

  isCustomTextOnPayButtonAllowed$: Observable<boolean> =
    this._orderformFascade.config$.pipe(
      map((data) => data.is_pay_button_label_changed_allowed)
    );

  private _changeTick$ = new ReplaySubject(1);

  needToShowOrderSummaryBeforePurchaseByConfig$ =
    this._orderformFascade.config$.pipe(
      map((data) => {
        return data.show_order_summary_before_purchase;
      })
    );

  isOrderSummaryAndProductListInViewPort$ = this._ngAfterViewInit$.pipe(
    switchMap(() => viewportVisibilityChange(this._elRef.nativeElement, 100)),
    filter((buyButtonIsVisible) => buyButtonIsVisible),
    share(),
    map(() => {
      return this._isOrderSummaryAndProductListInViewPort();
    })
  );

  needToShowOrderSummaryBeforePurchase$: Observable<boolean> = combineLatest([
    this.needToShowOrderSummaryBeforePurchaseByConfig$,
    this.isOrderSummaryAndProductListInViewPort$,
  ]).pipe(
    map(
      ([
        show_order_summary_before_purchase,
        forceAllowPurchaseWithoutSummary,
      ]) => {
        if (forceAllowPurchaseWithoutSummary) {
          return false;
        }
        return show_order_summary_before_purchase;
      }
    ),
    shareReplay(1)
  );

  showSummary$: Observable<boolean> = this._changeTick$.pipe(
    switchMap(() => this.needToShowOrderSummaryBeforePurchase$),
    map((needOverViewBeforePurchase: boolean) => {
      if (this.forceShowSummary) {
        return this.forceShowSummary === 'true';
      }

      if (this.summarySetting === 'above_always') {
        return true;
      }

      if (this.summarySetting === 'above_if_needed') {
        return needOverViewBeforePurchase;
      }

      if (this.summarySetting === 'popup_if_needed') {
        return false;
      }
    })
  );

  showOrderSummaryBeforePurchaseButtonText$: Observable<string> =
    this._orderformFascade.config$.pipe(
      map((data) => data.payment_button_label_overwrite)
    );

  orderSummaryButtonTextIfNeeded$: Observable<string | false> = combineLatest([
    this.needToShowOrderSummaryBeforePurchase$,
    this.showSummary$,
    this.showOrderSummaryBeforePurchaseButtonText$,
  ]).pipe(
    map(([needToShowSummary, isSummaryShown, purchaseText]) => {
      if (needToShowSummary && isSummaryShown === false) {
        return purchaseText;
      }
      return false;
    })
  );

  buttonText$ = combineLatest([
    this.isCustomTextOnPayButtonAllowed$,
    this.selectedPaymentMethod$,
  ]).pipe(
    map(([customTextAllowed, selectedPaymentProv]) => {
      // void == free product
      if (selectedPaymentProv.pay_method === 'void') {
        return $localize`:@@orderform.get_now_for_free:Jetzt kostenlos sichern`;
      }

      // check is allowed to use custom button text
      if (customTextAllowed && this.buyBtnText) {
        return this.buyBtnText;
      } else {
        // use text of selected payment prov.
        // fallback customButtonText
        return (
          selectedPaymentProv.label_data?.default_submit_button.replace(
            '|',
            '<br/>'
          ) ||
          this.buyBtnText ||
          $localize`:@@orderform.buy_now:Jetzt kaufen`
        );
      }
    })
  );

  paymentLegalHint$ = combineLatest([
    this.buttonText$,
    this._orderformFascade.paymentLegalHint$,
  ]).pipe(
    map(([buttonText, hint]) =>
      hint ? hint.replace('__BUTTON__', buttonText) : null
    )
  );

  constructor(
    private _orderformFascade: OrderformFascade,
    private _store: Store<OrderformState>,
    private _ngZone: NgZone,
    private _iframeService: OrderformIframeService,
    private _elRef: ElementRef,
    private _cdr: ChangeDetectorRef,
    private _iframeModal: IframeModalService,
    private _monitoring: MonitoringService
  ) {}

  ngAfterViewInit() {
    this._ngAfterViewInit$.next();
  }

  ngOnChanges() {
    this._changeTick$.next(true);
  }

  private _isOrderSummaryAndProductListInViewPort() {
    /**
     * Check only force buy if button is rendered once!
     */
    const testToForce: TypeSummarySetting[] = [
      'above_if_needed',
      'popup_if_needed',
    ];

    if (
      isElementInViewport(this._elRef.nativeElement, 0) &&
      testToForce.includes(this.summarySetting)
    ) {
      const isElementVisible = (el): boolean => {
        return !!el.getBoundingClientRect().height;
      };

      const filterCond = (element: HTMLElement): boolean => {
        return isElementVisible(element) && isElementInViewport(element, 0);
      };

      // Alle relevanten Elemente der Seite durchgehen und prüfen, ob sie vollständig sichtbar sind
      const productListsEls = Array.from(
        document.getElementsByTagName('ds-orderform-product-list')
      ).filter(filterCond);

      const orderSummaryEls = Array.from(
        document.getElementsByTagName('ds-orderform-order-summary')
      ).filter(filterCond);

      if (orderSummaryEls.length && productListsEls.length) {
        return true;
      }
    }

    return false;
  }

  async checkSummaryModalShouldShow() {
    const needToShowOrderSummaryBeforePurchase = await firstValueFrom(
      this.needToShowOrderSummaryBeforePurchase$
    );
    const isSummaryShown = await firstValueFrom(this.showSummary$);
    if (needToShowOrderSummaryBeforePurchase && isSummaryShown === false) {
      this.modalVisible = true;
      return false;
    }
    return true;
  }

  /**
   * Submit OF
   * @param checkModalNeeded if true, check before submit is confirm purchase modal needed else skip check
   */
  async submit(checkModalNeeded: boolean = false) {
    if (this._isEditorMode) {
      return;
    }

    /**
     * Block double clicks on buy button
     */
    this._blockButton$.next(true);
    this._monitoring.trackUserInteraction('BuyButtonClick');

    // Check Validations
    const canSubmit = await firstValueFrom(
      this._orderformFascade.beforeSubmitOrderForm()
    );

    if (!canSubmit) {
      await new Promise((resolve) => {
        this._ngZone.runOutsideAngular(() =>
          setTimeout(() => {
            const firstElement = document.querySelector(
              'orderform-input-errors .error, ds-orderform-buy-button .error'
            );
            if (firstElement) {
              firstElement.scrollIntoView({ block: 'center' });
              this._iframeService.scrollToIframeElementPosition(
                firstElement.getBoundingClientRect().top
              );
            }
            resolve(true);
          })
        );
      });
      this._blockButton$.next(false);
      this._orderformFascade.submitFailed.emit();
      return false;
    }

    /**
     * Modal for summary before purchase check
     */
    if (
      checkModalNeeded &&
      (await this.checkSummaryModalShouldShow()) === false
    ) {
      this._blockButton$.next(false);
      return false;
    }

    // Close confirm for loading spinner
    this.modalVisible = false;
    this._cdr.detectChanges();

    this._monitoring.trackUserInteraction('BuySubmit');
    const sendSuccess = await firstValueFrom(
      this._orderformFascade.submitOrderForm()
    );
    // unblock
    this._blockButton$.next(false);

    // Nachdem das Formular abgesendet wurde kann es im Backend noch zu Fehler gekommen sein
    const formStillValid = (
      await this._orderformFascade.formValidation.emit()
    ).reduce((valid, result) => {
      return valid && result.valid;
    }, true);

    if (!formStillValid) {
      this._orderformFascade.submitFailed.emit();
      return false;
    } else if (sendSuccess) {
      this._orderformFascade.afterSubmit.emit().then((results) => {
        if (
          !results.reduce((isValid, result) => isValid && result.valid, true)
        ) {
          this._store.dispatch(OrderformActions.afterSubmitOrderFormFailed());
        }
      });
    }
  }

  @HostListener('click', ['$event'])
  public openLegalLink(e: Event) {
    const anchor = e.target as Element;
    if (anchor.tagName !== 'A') {
      return;
    }
    e.preventDefault();
    this._iframeModal.open(
      anchor.getAttribute('title') ?? anchor.textContent,
      anchor.getAttribute('href')
    );
  }

  ngOnDestroy() {
    this._destroy$.next();
  }
}
